import { Branch, PaycloudBranch, BranchFiles } from "../types";
import BaseService from "./base-service";
import {
  AddBranchDTO,
  BranchesInformationReq,
  BranchesReqDTO,
  EditBranchDTO,
} from "./dto/request/branches-request-dto";
import { RequestDto } from "./dto/request/request-dto";
import {
  PaycloudKycCatalogs,
  BranchesInformationRes,
  GetBranchesResponse,
  BranchSettingsRes,
  SetupStylesParams,
  BranchForGroupsRes,
} from "./dto/response/branches-response-dt";

export class BranchService extends BaseService {
  private resource = "/api/branches";

  async getBranches(
    params: BranchesReqDTO,
    onSuccess: (data: GetBranchesResponse) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(this.resource + "/", params);
    await this.doGet<GetBranchesResponse>(reqParams, (data) => {
      // Mapear los archivos de la respuesta del backend a la estructura esperada
      const mappedData = {
        ...data,
        branches: data.branches.map((branch: any) => ({
          ...branch,
          // Mapear archivos del objeto files a propiedades directas
          managerDniFront: branch.files?.managerDniFront || null,
          managerDniBack: branch.files?.managerDniBack || null,
          managerSelfie: branch.files?.managerSelfie || null,
          afipFile: branch.files?.afipFile || null,
          fantasyName: branch.fantasyName || branch.name,
          googleLink: branch.googleLink || branch.GoogleLink || null,
        }))
      };
      onSuccess(mappedData);
    }, onError);
  }

  async addBranch(
    params: AddBranchDTO,
    onSuccess: (data: Branch) => void,  
    onError: (errorMessage: string) => void
  ) {
    const formData = new FormData();
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const reqParams = new RequestDto(this.resource, formData, {
      "Content-Type": "multipart/form-data",
    });

    await this.doPost<Branch>(reqParams, onSuccess, onError);
  }

  async deleteBranch(
    branchId: number,
    onSuccess: () => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(`${this.resource}/${branchId}`, {});
    await this.doDelete<Branch[]>(reqParams, onSuccess, onError);
  }

  async updateBranch(
    params: EditBranchDTO,
    onSuccess: (data: any) => void,
    onError: (errorMessage: string) => void
  ) {
    const formData = new FormData();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'boolean') {
          formData.append(key, value.toString());
        } else if (typeof value === 'number') {
          formData.append(key, value.toString());
        } else if (typeof value === 'string') {
          formData.append(key, value);
        } else {
          formData.append(key, value.toString());
        }
      } else if (value === null) {
        formData.append(key, "null");
      }
    });

    const reqParams = new RequestDto(this.resource, formData, { "Content-Type": "multipart/form-data" });
    await this.doPatch<any>(reqParams, onSuccess, onError);
  }

  async getBranchesInformation(
    params: BranchesInformationReq,
    onSuccess: (data: BranchesInformationRes[]) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto("/api/getFristLoginData/", params);
    await this.doGet<BranchesInformationRes[]>(reqParams, onSuccess, onError);
  }

  async getBranchSettings(
    params: { branchId: number },
    onSuccess: (data: BranchSettingsRes[]) => void,
    onError: (errorMessage: string) => void
  ) {
    const branchId = Number(params.branchId);
    if (isNaN(branchId) || branchId <= 0) {
      onError('Error: ID de sucursal inválido');
      return;
    }
    const reqParams = new RequestDto(`${this.resource}/settings/${branchId}`, {});
    await this.doGet<BranchSettingsRes[]>(reqParams, onSuccess, onError);
  }

  async setupStyles(
    params: SetupStylesParams,
    onSuccess: (data: any) => void,
    onError: (errorMessage: string) => void
  ) {
    const branchId = Number(params.branchId);
    if (isNaN(branchId) || branchId <= 0) {
      onError('Error: El ID de sucursal debe ser un número positivo');
      return;
    }

    try {
      const formData = new FormData();
      
      formData.append('textLandingColor', params.textLandingColor);
      formData.append('headerLandingColor', params.headerLandingColor);
      formData.append('branchId', branchId.toString());
      
      if (params.photoLogo) {
        formData.append('photoLogo', params.photoLogo);
      }
      
      const reqParams = new RequestDto(this.resource + "/styles", formData, {
        "Content-Type": "multipart/form-data"
      });
      
      await this.doPatch<any>(reqParams, onSuccess, onError);
    } catch (error) {
      onError('Error al configurar los estilos de la sucursal');
    }
  }

  async getBranchFiles(
    branchId: number,
    onSuccess: (data: BranchFiles) => void,
    onError: (errorMessage: string) => void
  ) {
    if (isNaN(branchId) || branchId <= 0) {
      onError('Error: ID de sucursal inválido');
      return;
    }

    const reqParams = new RequestDto(`${this.resource}/${branchId}/files`, {});
    await this.doGet<BranchFiles>(reqParams, onSuccess, onError);
  }

  async getKycCatalogs(
    onSuccess: (data: PaycloudKycCatalogs) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto("/api/paycloud/settings/kyc", {});
    await this.doGet<PaycloudKycCatalogs>(reqParams, onSuccess, onError);
  }

  async getBranchesForGroups(
    onSuccess: (data: BranchForGroupsRes[]) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto("/api/groups/branches", {});
    await this.doGet<BranchForGroupsRes[]>(reqParams, onSuccess, onError);
  }

  async updateManualProcessTips(
    branchId: number,
    manualTipsProccess: boolean,
    onSuccess: (data: any) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      `${this.resource}/manual-tips-process`,
      {
        BranchId: branchId,
        ManualTipsProccess: manualTipsProccess,
      },
      {
        "Content-Type": "application/json",
      }
    );
    await this.doPatch<any>(reqParams, onSuccess, onError);
  }

  async getManualTipsReprocessStatus(
    branchId: number,
    onSuccess: (data: ManualTipsReprocessStatus) => void,
    onError: (errorMessage: string) => void,
    managerId?: number
  ) {
    const params = managerId !== undefined ? { ManagerId: managerId } : {};
    const reqParams = new RequestDto(
      `${this.resource}/${branchId}/manual-tips-reprocess/status`,
      params,
      { "Content-Type": "application/json" }
    );
    await this.doGet<ManualTipsReprocessStatus>(reqParams, onSuccess, onError);
  }

  async getPendingTipsManualProcess(
    branchId: number,
    onSuccess: (data: PendingManualTip[]) => void,
    onError: (errorMessage: string) => void,
    managerId?: number
  ) {
    const params = managerId !== undefined ? { ManagerId: managerId } : {};
    const reqParams = new RequestDto(
      `${this.resource}/${branchId}/manual-tips-pending`,
      params,
      { "Content-Type": "application/json" }
    );
    await this.doGet<PendingManualTip[]>(reqParams, onSuccess, onError);
  }

  async getEmployeeAvailabilityForTips(
    branchId: number,
    tipIds: number[],
    onSuccess: (data: EmployeeAvailabilityForTips) => void,
    onError: (errorMessage: string) => void,
    managerId?: number
  ) {
    const body: Record<string, unknown> = { TipIds: tipIds };
    if (managerId !== undefined) {
      body.ManagerId = managerId;
    }
    const reqParams = new RequestDto(
      `${this.resource}/${branchId}/employee-availability`,
      body,
      { "Content-Type": "application/json" }
    );
    await this.doPost<EmployeeAvailabilityForTips>(reqParams, onSuccess, onError);
  }

  async saveEmployeeAvailabilityForDates(
    branchId: number,
    items: EmployeeAvailabilitySaveItem[],
    onSuccess: (data: { message: string }) => void,
    onError: (errorMessage: string) => void,
    managerId?: number
  ) {
    const body: Record<string, unknown> = { Items: items };
    if (managerId !== undefined) {
      body.ManagerId = managerId;
    }
    const reqParams = new RequestDto(
      `${this.resource}/${branchId}/employee-availability`,
      body,
      { "Content-Type": "application/json" }
    );
    await this.doPut<{ message: string }>(reqParams, onSuccess, onError);
  }

  async retryPendingTips(
    branchId: number,
    onSuccess: (data: any) => void,
    onError: (
      errorMessage: string,
      errorCode?: number,
      errorData?: unknown
    ) => void,
    managerId?: number,
    tipIds?: number[]
  ) {
    const body: Record<string, unknown> = {};
    if (managerId !== undefined) {
      body.ManagerId = managerId;
    }
    if (tipIds && tipIds.length > 0) {
      body.TipIds = tipIds;
    }

    if (tipIds && tipIds.length > 0) {
      const reqParams = new RequestDto(
        `${this.resource}/retry-pending-tips/${branchId}`,
        body,
        {
          "Content-Type": "application/json",
        }
      );
      await this.doPost<any>(reqParams, onSuccess, (message, code, data) =>
        onError(message, code, data)
      );
      return;
    }

    const params = managerId !== undefined ? { ManagerId: managerId } : {};
    const reqParams = new RequestDto(
      `${this.resource}/retry-pending-tips/${branchId}`,
      params,
      {
        "Content-Type": "application/json",
      }
    );
    await this.doGet<any>(reqParams, onSuccess, (message, code, data) =>
      onError(message, code, data)
    );
  }

  async getProcessTipsHistory(
    branchId: number,
    onSuccess: (data: ProcessTipsHistoryItem[]) => void,
    onError: (errorMessage: string) => void,
    managerId?: number
  ) {
    const params = managerId !== undefined ? { ManagerId: managerId } : {};
    const reqParams = new RequestDto(
      `${this.resource}/${branchId}/tips-processing-history`,
      params,
      { "Content-Type": "application/json" }
    );
    await this.doGet<TipsProcessingHistoryResponseItem[]>(reqParams, (raw) => {
      const normalized = (Array.isArray(raw) ? raw : []).map((item) => ({
        id: item.tipsProcessingHistoryId,
        createdAt:
          typeof item.createdDate === "string"
            ? item.createdDate
            : item.createdDate instanceof Date
              ? item.createdDate.toISOString()
              : "",
        totalTipsCount: item.totalTipsCount ?? 0,
        failedTipsCount: item.failedTipsCount ?? 0,
        completed: item.completedDate != null,
      }));
      onSuccess(normalized);
    }, onError);
  }

  async validateEmployees(
    branchId: number,
    onSuccess: (data: ValidateEmployeesResponse) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      `${this.resource}/validate-employees/${branchId}`,
      {},
      {
        "Content-Type": "application/json",
      }
    );
    await this.doGet<ValidateEmployeesResponse>(reqParams, onSuccess, onError);
  }

  async setBranchEmployeesQrDisplayConfig(
    branchId: number,
    qrDisplayConfigId: number,
    onSuccess: (data: { updated: number }) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      `${this.resource}/${branchId}/employee-qr-display-config`,
      { QrDisplayConfigId: qrDisplayConfigId }
    );
    await this.doPut<{ updated: number }>(reqParams, onSuccess, onError);
  }
}

export interface TipsProcessingHistoryResponseItem {
  tipsProcessingHistoryId: number;
  branchId: number;
  active: boolean;
  createdDate: string | Date;
  updatedDate: string | Date;
  userId: number;
  completedDate?: string | Date | null;
  summaryEmailSentAt?: string | Date | null;
  totalTipsCount?: number;
  failedTipsCount?: number;
}

export interface ProcessTipsHistoryItem {
  id?: number;
  createdAt: string;
  totalTipsCount?: number;
  failedTipsCount?: number;
  completed?: boolean;
}

export interface PendingManualTip {
  tipId: number;
  amount: string | number;
  status: number;
  statusDescription: string;
  createdDate: string | Date | null;
  externalTipCreatedDate: string | Date | null;
  tipTypeId: number | null;
  employeeName: string | null;
  originName: string | null;
}

export interface EmployeeAvailabilityForTips {
  dates: string[];
  employees: {
    userId: number;
    name: string;
    availabilityByDate: Record<string, boolean>;
  }[];
}

export interface EmployeeAvailabilitySaveItem {
  UserId: number;
  AvailabilityDate: string;
  OnLeave: boolean;
}

export type ManualTipsReprocessReason = "IN_PROCESS" | "COOLDOWN" | null;

export interface ManualTipsReprocessStatus {
  canReprocess: boolean;
  reason: ManualTipsReprocessReason;
  secondsRemaining: number;
  pendingTipsCount: number;
  inProcessCount: number;
  lastReprocessAt: string | null;
  cooldownSeconds: number;
}

export interface EmployeeIssue {
  userId: number;
  name: string;
  email: string;
  isVerified: boolean;
  hasSchedule: boolean;
  reason: string;
}

export interface ValidateEmployeesResponse {
  valid: boolean;
  message?: string;
  employeesWithIssues?: EmployeeIssue[];
}
